import { Button, Skeleton, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function OutboundWavelessPreviewPage() {
	const documentTypeId = useNumberParam('documentTypeId');
	const queueId = useNumberParam('queueId');
	const client = useTerminalClient();
	const navigate = useNavigate();

	const nextPickQuery = useQuery({
		queryKey: ['waveless-suggest-next-pick', documentTypeId],
		queryFn: () =>
			client.outboundDeliveryOrders.outboundWavelessSuggestNextPickQuery.execute({
				documentTypeId: documentTypeId!,
				outboundQueueId: queueId
			}),
		enabled: !!documentTypeId
	});

	const nextPick = nextPickQuery.data;

	const documentType = useQuery({
		queryKey: ['outbound-document-type', documentTypeId],
		queryFn: () => client.outboundDeliveryOrderDocumentTypes.getById.execute({ id: documentTypeId! }),
		enabled: !!documentTypeId
	});

	const assignMutation = useMutation({
		mutationFn: () => {
			if (!nextPick?.suggestedLocation?.id) {
				throw new Error('No location selected');
			}
			return client.outboundDeliveryOrders.outboundWavelessAssignNextPickToPickerMutation.execute({
				documentTypeId: documentTypeId!,
				locationId: nextPick.suggestedLocation.id,
				outboundQueueId: queueId
			});
		},
		onSuccess: () => navigate(`/outbound/${documentTypeId}/waveless/${queueId}/process`)
	});

	const leaveDocumentTypeMutation = useMutation({
		mutationFn: () => client.outboundDeliveryOrders.outboundWavelessLeaveDocumentTypeMutation.execute({}),
		onSuccess: () => navigate(`/outbound/${documentTypeId}/queues`)
	});

	useValidationResultDialog([nextPickQuery.error, assignMutation.error, leaveDocumentTypeMutation.error]);

	const goToList = () => navigate(`/outbound/${documentTypeId}/waveless/${queueId}/list`);
	const goToUnload = () => navigate(`/outbound/${documentTypeId}/waveless/${queueId}/unload`);

	// Custom back button with leave document type functionality
	const customBackButton = useMemo(
		() => (
			<Button
				variant="ghost"
				size="icon"
				onClick={() => leaveDocumentTypeMutation.mutate()}
				disabled={leaveDocumentTypeMutation.isPending}
				loading={leaveDocumentTypeMutation.isPending}
			>
				<ArrowLeft className="h-4 w-4" />
			</Button>
		),
		[leaveDocumentTypeMutation]
	);

	return (
		<TerminalScreen showBackButton={false} hideBackButton={true} customBackButton={customBackButton} topBar={documentType.data?.name}>
			<div className="p-2 flex flex-col flex-1 overflow-auto h-full">
				<div className="flex flex-col bg-card p-2 shadow-md gap-2">
					{nextPickQuery.isLoading ? (
						<>
							<Skeleton className="h-6 w-40" />
							<Skeleton className="h-6 w-64" />
						</>
					) : nextPick ? (
						<>
							<div>
								<div className="font-semibold">
									<StockItemLinkDetail stockItemId={nextPick.suggestedLocation.stockItem.id} />
								</div>
								<div className="text-sm text-muted-foreground">{nextPick.suggestedLocation.stockItem.item.code}</div>
							</div>
							<div className="flex flex-row ">
								<div className="w-1/2">Lokace</div>
								<div className="font-semibold w-1/2">{nextPick.suggestedLocation.storageLocation.name}</div>
							</div>
							<div className="flex flex-row ">
								<div className="w-1/2">Paleta</div>
								<div className="font-semibold w-1/2">{nextPick.suggestedLocation.handlingUnit?.code ?? '-'}</div>
							</div>
							<div className="flex flex-row ">
								<div className="w-1/2">Zbývá</div>
								<div className="font-semibold w-1/2">{nextPick.quantityToPick} ks</div>
							</div>

							<div className="my-2">
								<Button
									className="w-full"
									onClick={() => assignMutation.mutate()}
									disabled={assignMutation.isPending || !documentTypeId || !nextPick?.suggestedLocation?.id}
								>
									Další položka
								</Button>
							</div>
						</>
					) : (
						<div className="space-y-2">
							<div className="p-6 text-center text-muted-foreground">Žádné návrhy</div>
						</div>
					)}
				</div>

				<div className="flex-grow"></div>

				<div className="flex flex-col gap-4 p-4">
					<Button className="w-full" variant="outline" onClick={goToList}>
						Seznam
					</Button>
					<Button className="w-full" variant="outline" onClick={goToUnload}>
						Vyložení
					</Button>
				</div>
			</div>
		</TerminalScreen>
	);
}
